import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchHorse, exportHorseCSV } from "../lib/api";
import RaceHistoryTable from "../components/RaceHistoryTable";
import PerformanceChart from "../components/PerformanceChart";
import WinLossStats from "../components/WinLossStats";
import SearchBar from "../components/SearchBar";
import {
  Horse as HorseIcon, ArrowLeft, DownloadSimple, User, Trophy, Calendar, FileCsv,
} from "@phosphor-icons/react";

const TRACK_IMG = "https://images.pexels.com/photos/1462398/pexels-photo-1462398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function HorseProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchHorse(id)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="mono-label text-zinc-400">Loading horse data...</p>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 text-sm mb-4">Horse not found</p>
          <Link to="/" className="text-sm font-mono underline">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const { horse, races, jockey, stats } = data;

  return (
    <div data-testid="horse-profile-page" className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-border px-4 md:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
        <Link to="/" className="flex items-center gap-2">
          <HorseIcon weight="fill" size={28} className="text-[#E12B00]" />
          <span className="text-lg font-heading font-black tracking-tight text-zinc-900">Racing Form Guide</span>
        </Link>
        <SearchBar />
      </header>

      <main className="p-4 md:p-8 max-w-[1400px] mx-auto">
        {/* Back link */}
        <Link to="/" data-testid="back-to-dashboard" className="inline-flex items-center gap-1 text-xs font-mono text-zinc-400 hover:text-zinc-900 transition-colors mb-6">
          <ArrowLeft weight="bold" size={14} />
          Back to Dashboard
        </Link>

        {/* Hero Banner */}
        <div className="relative h-[200px] md:h-[260px] border border-border overflow-hidden mb-6">
          <img src={TRACK_IMG} alt="Track" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <p className="mono-label text-zinc-400 mb-1">{horse.age}yo {horse.sex} &middot; {horse.owner}</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-white tracking-tighter">{horse.name}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-zinc-300 font-mono">
              <span className="flex items-center gap-1"><User weight="bold" size={14} />{horse.trainer}</span>
              <span className="flex items-center gap-1"><Trophy weight="fill" size={14} className="text-[#008A27]" />{horse.total_wins}W / {horse.total_races}R</span>
              <span>Form: {horse.form}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6">
          <WinLossStats stats={stats} />
        </div>

        {/* Grid: Chart + Jockey */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2">
            <PerformanceChart races={races} />
          </div>

          {/* Jockey Details */}
          <div className="border border-border p-4 bg-white" data-testid="jockey-details">
            <p className="mono-label text-zinc-400 mb-3">Jockey</p>
            {jockey ? (
              <Link to={`/jockey/${jockey.id}`} className="group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-zinc-100 border border-border flex items-center justify-center overflow-hidden">
                    <User weight="bold" size={24} className="text-zinc-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900 group-hover:text-[#E12B00] transition-colors">{jockey.name}</p>
                    <p className="text-xs text-zinc-500">{jockey.nationality} &middot; Age {jockey.age}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-zinc-50 p-3 border border-border">
                    <p className="mono-label text-zinc-400 text-[10px]">Career Wins</p>
                    <p className="font-mono text-lg font-bold text-[#008A27]">{jockey.total_wins?.toLocaleString()}</p>
                  </div>
                  <div className="bg-zinc-50 p-3 border border-border">
                    <p className="mono-label text-zinc-400 text-[10px]">Total Rides</p>
                    <p className="font-mono text-lg font-bold">{jockey.total_rides?.toLocaleString()}</p>
                  </div>
                </div>
              </Link>
            ) : (
              <p className="text-sm text-zinc-400">Jockey details unavailable</p>
            )}
          </div>
        </div>

        {/* Race History */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="mono-label text-zinc-500">
              <Calendar weight="bold" size={12} className="inline mr-1" />
              Last {races.length} Races
            </p>
            <a
              href={exportHorseCSV(id)}
              data-testid="export-csv-button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-xs font-mono uppercase tracking-wider hover:bg-[#09090B] hover:text-white transition-colors"
            >
              <FileCsv weight="bold" size={14} />
              Export CSV
            </a>
          </div>
          <RaceHistoryTable races={races} />
        </div>
      </main>
    </div>
  );
}
